import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/kibo-ui/combobox';
import { cn } from '@/lib/utils';
import { ChevronsUpDownIcon, PaletteIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import imageStylesData from '@/lib/image-styles.json';
import { useI18n } from '@/providers/i18n';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';

type StyleOption = {
  value: string;
  label: string;
  prompt: string;
  description?: string;
};

const STYLE_PRESETS: StyleOption[] = imageStylesData.styles;

type StyleMode = 'txt2img' | 'img2img';

function getPresetsForModel(modelId?: string, mode?: StyleMode): StyleOption[] {
  try {
    const data: any = imageStylesData as any;
    const modelStyles = data.modelStyles || {};
    const modeStyles = data.modeStyles || {};

    let allowedValues: string[] | undefined;

    // Prefer model-specific mapping
    if (modelId && modelStyles[modelId]) {
      const ms = modelStyles[modelId];
      if (Array.isArray(ms)) {
        allowedValues = ms as string[];
      } else if (ms && typeof ms === 'object' && mode) {
        // Support multiple key variants for safety
        const byMode = ms[mode] || ms[mode === 'txt2img' ? 'textToImage' : 'imageToImage'];
        if (Array.isArray(byMode)) {
          allowedValues = byMode as string[];
        }
      }
    }

    // Next preference: global per-mode mapping
    if (!allowedValues && mode) {
      const globalByMode = modeStyles[mode] || modeStyles[mode === 'txt2img' ? 'textToImage' : 'imageToImage'];
      if (Array.isArray(globalByMode)) {
        allowedValues = globalByMode as string[];
      }
    }

    if (!allowedValues || allowedValues.length === 0) return STYLE_PRESETS;

    const alwaysInclude = new Set(['none', 'custom']);
    const allowed = new Set<string>([...allowedValues, ...alwaysInclude]);
    const filtered = STYLE_PRESETS.filter((s) => allowed.has(s.value));
    return filtered.length ? filtered : STYLE_PRESETS;
  } catch {
    return STYLE_PRESETS;
  }
}

type StyleSelectorProps = {
  id?: string;
  value: string;
  width?: number | string | "auto";
  className?: string;
  onChange?: (value: string, prompt: string) => void;
  disabled?: boolean;
  modelId?: string;
  mode?: StyleMode;
};

export const StyleSelector = ({
  id,
  value,
  width = 200,
  className,
  onChange,
  disabled,
  modelId,
  mode,
}: StyleSelectorProps) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const isPreview = usePreviewCanvas();
  const effectiveDisabled = Boolean(disabled || isPreview);
  const filteredPresets = useMemo(() => getPresetsForModel(modelId, mode), [modelId, mode]);
  
  const selectedStyle =
    filteredPresets.find((style) => style.value === value) ||
    STYLE_PRESETS.find((style) => style.value === value) ||
    filteredPresets[0] ||
    STYLE_PRESETS[0];

  return (
    <Combobox
      open={open}
      onOpenChange={(next) => {
        if (effectiveDisabled) return;
        setOpen(next);
      }}
      data={filteredPresets.map((style) => ({
        label: style.value === 'none' ? t('No Style') : style.value === 'custom' ? t('Custom') : style.label,
        value: style.value,
      }))}
      type="style"
      value={value}
      onValueChange={(val) => {
        const style = (filteredPresets.find((s) => s.value === val) || STYLE_PRESETS.find((s) => s.value === val));
        if (style) {
          onChange?.(val, style.prompt);
        }
        setOpen(false);
      }}
    >
      <ComboboxTrigger 
        id={id} 
        variant="ghost" 
        className={cn('rounded-md h-8', className)} 
        style={width === "auto" ? {} : { width }} 
        disabled={effectiveDisabled}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <PaletteIcon size={14} className="text-muted-foreground" />
            <span className="whitespace-nowrap">{selectedStyle.value === 'none' ? t('No Style') : selectedStyle.value === 'custom' ? t('Custom') : selectedStyle.label}</span>
          </div>
          <ChevronsUpDownIcon size={14} className="shrink-0 text-muted-foreground" />
        </div>
      </ComboboxTrigger>
      <ComboboxContent
        className="p-0"
        popoverOptions={{ className: 'rounded-md border bg-popover p-0 shadow-md' }}
      >
        <ComboboxInput className="h-9" />
        <ComboboxList>
          <ComboboxEmpty />
          <ComboboxGroup>
            {filteredPresets.map((style) => (
              <ComboboxItem
                key={style.value}
                value={style.value}
                onSelect={() => {
                  onChange?.(style.value, style.prompt);
                  setOpen(false);
                }}
                className="gap-2 py-1.5"
              >
                <PaletteIcon size={14} className="text-muted-foreground" />
                <div className="flex min-w-0 flex-col">
                  <span className="block truncate leading-5">{style.value === 'none' ? t('No Style') : style.value === 'custom' ? t('Custom') : style.label}</span>
                  {style.description ? (
                    <span className="block truncate text-xs leading-4 text-muted-foreground">
                      {style.description}
                    </span>
                  ) : null}
                </div>
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
